import * as i0 from '@angular/core';
import { EventEmitter, Component, ViewEncapsulation, ChangeDetectionStrategy, Input, Output, HostListener, Injectable, NgModule } from '@angular/core';
import * as i1$1 from '@ngrx/store';
import { compose, StoreModule } from '@ngrx/store';
import { type, distinct, getLeagueParticipants, entryAvailable, LoadLeagueOnceAction, getLeague, exists, LoadParticipantsOnceAction, getUser, LoadContestantsOnceAction, getContestants, StateModule } from '@play/state';
import { combineLatest, BehaviorSubject, of } from 'rxjs';
import { includes, sortBy, keyBy, minBy } from 'lodash';
import { filter, map, withLatestFrom, switchMap, mergeMap, skip, takeUntil, take, tap } from 'rxjs/operators';
import * as i2 from '@play/common';
import { ESCAPE, CommonsErrorModule, CommonsLoadingModule, CommonsComponentModule } from '@play/common';
import { isTeam } from '@play/utility/league';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i4 from '@play/translate';
import { TranslationModule } from '@play/translate';
import { Map } from 'immutable';
import { __decorate, __metadata } from 'tslib';
import * as i1$2 from '@ngrx/effects';
import { ofType, Effect, EffectsModule } from '@ngrx/effects';

const SelectedParticipantActionTypes = {
    INIT: type("[Cup Page] Init Selected Participant"),
    SELECT: type("[Cup Page] Select Participant"),
};
class InitSelectedParticipantAction {
    constructor(leagueId, selector) {
        this.leagueId = leagueId;
        this.selector = selector;
        this.type = SelectedParticipantActionTypes.INIT;
    }
}
class SelectParticipantAction {
    constructor(leagueId, participantId) {
        this.leagueId = leagueId;
        this.participantId = participantId;
        this.type = SelectedParticipantActionTypes.SELECT;
    }
}

const isCheckedIn = (participant) => participant && participant.status === "checkedIn";
/**
 * Checks if a participant is playing
 *
 * @param participants
 */
function isPlaying(participants) {
    return compose(isCheckedIn, participantId => participants.find(p => p.id === participantId));
}
/**
 * Get the currently selected participant
 *
 * @param state
 */
const getParticipantFromState = (leagueId) => (state) => state.cupPage.selectedParticipant ? state.cupPage.selectedParticipant.get(leagueId) : null;
/**
 * Gets the correct participant id from the available data.
 */
function getSelectedParticipant(leagueId, preselectedParticipants = []) {
    return (state$) => {
        const participantsData$ = combineLatest(state$.pipe(distinct(getLeagueParticipants(leagueId)), filter(entryAvailable)), state$.pipe(distinct(state => getParticipantFromState(leagueId)(state)), map(state => [...preselectedParticipants, state])));
        return participantsData$.pipe(map(([participants, preselectedContestants]) => {
            const isPartcipantPlaying = isPlaying(participants.entry);
            const preselectedContestant = preselectedContestants.find(isPartcipantPlaying);
            if (preselectedContestant) {
                return preselectedContestant;
            }
            return null;
        }));
    };
}

function ParticipantDropdownComponent_div_6_Template(rf, ctx) { if (rf & 1) {
    const _r4 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 8);
    i0.ɵɵelementStart(1, "div", 9);
    i0.ɵɵtext(2);
    i0.ɵɵpipe(3, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "ul", 6);
    i0.ɵɵelementStart(5, "li", 10);
    i0.ɵɵelementStart(6, "a", 11);
    i0.ɵɵlistener("click", function ParticipantDropdownComponent_div_6_Template_a_click_6_listener($event) { i0.ɵɵrestoreView(_r4); const ctx_r3 = i0.ɵɵnextContext(); ctx_r3.selectParticipant(ctx_r3.ownContestant); return $event.preventDefault(); });
    i0.ɵɵtext(7);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(8, "span", 12);
    i0.ɵɵtext(9);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(3, 3, "participant-dropdown:me"), " ");
    i0.ɵɵadvance(5);
    i0.ɵɵtextInterpolate1(" ", ctx_r0.participants[ctx_r0.ownContestant].name, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" (", ctx_r0.participants[ctx_r0.ownContestant].seed, ") ");
} }
const _c0$1 = function (a0) { return { isTeam: a0 }; };
function ParticipantDropdownComponent_div_7_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 13);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 1, "participant-dropdown:top-contestants", i0.ɵɵpureFunction1(4, _c0$1, ctx_r1.isTeam ? 1 : 0)), " ");
} }
function ParticipantDropdownComponent_li_9_Template(rf, ctx) { if (rf & 1) {
    const _r7 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "li", 10);
    i0.ɵɵelement(1, "country-flag", 14);
    i0.ɵɵelementStart(2, "a", 15);
    i0.ɵɵlistener("click", function ParticipantDropdownComponent_li_9_Template_a_click_2_listener($event) { i0.ɵɵrestoreView(_r7); const participant_r5 = ctx.$implicit; const ctx_r6 = i0.ɵɵnextContext(); ctx_r6.selectParticipant(participant_r5.id); return $event.preventDefault(); });
    i0.ɵɵtext(3);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(4, "span", 12);
    i0.ɵɵtext(5);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const participant_r5 = ctx.$implicit;
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("country", participant_r5.region);
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" ", participant_r5.name, " ");
    i0.ɵɵadvance(2);
    i0.ɵɵtextInterpolate1(" (", participant_r5.seed, ") ");
} }
function filterParticipantsByName(participants, searchText) {
    if (!searchText) {
        return participants;
    }
    return participants.filter(participant => includes(participant.name.toLowerCase(), searchText.toLowerCase()));
}
class ParticipantDropdownComponent {
    constructor() {
        this.selected = new EventEmitter();
    }
    ngOnChanges(changes) {
        if (changes.participants) {
            this.sortedParticipants = sortBy(this.participants, (participant) => {
                return participant.seed;
            });
        }
    }
    ngOnInit() {
        this.filteredParticipants = this.filterParticipants(this.sortedParticipants, this.searchText);
    }
    searchPartcipant(text) {
        this.searchText = text;
        this.filteredParticipants = this.filterParticipants(this.sortedParticipants, this.searchText);
    }
    selectParticipant(id) {
        this.selected.emit(+id);
    }
    trackById(index, item) {
        return item.id;
    }
    filterParticipants(participants, searchText) {
        return filterParticipantsByName(participants, searchText);
    }
}
ParticipantDropdownComponent.ɵfac = function ParticipantDropdownComponent_Factory(t) { return new (t || ParticipantDropdownComponent)(); };
ParticipantDropdownComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ParticipantDropdownComponent, selectors: [["cup-page-participant-dropdown"]], inputs: { participants: "participants", ownContestant: "ownContestant", isTeam: "isTeam" }, outputs: { selected: "selected" }, features: [i0.ɵɵNgOnChangesFeature], decls: 10, vars: 10, consts: [[1, "c-participant-dropdown", 3, "click"], [1, "c-participant-dropdown__search"], ["for", "contestant", 1, "c-participant-dropdown__search__label"], ["id", "contestant", "type", "text", 1, "c-participant-dropdown__search__input", 3, "ngModel", "autofocus", "ngModelChange"], ["class", "c-participant-dropdown__me", 4, "ngIf"], ["class", "c-participant-dropdown__top-players", 4, "ngIf"], [1, "c-participant-dropdown__contestants"], ["class", "c-participant-dropdown__contestant", 4, "ngFor", "ngForOf"], [1, "c-participant-dropdown__me"], [1, "c-participant-dropdown__me__title"], [1, "c-participant-dropdown__contestant"], ["href", "#", 3, "click"], [1, "c-participant-dropdown__contestant__seed"], [1, "c-participant-dropdown__top-players"], [1, "cup-match__result__player__flag", 3, "country"], ["href", "#", 1, "c-participant-dropdown__contestant__name", 3, "click"]], template: function ParticipantDropdownComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵlistener("click", function ParticipantDropdownComponent_Template_div_click_0_listener($event) { return $event.stopPropagation(); });
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelementStart(2, "label", 2);
        i0.ɵɵtext(3);
        i0.ɵɵpipe(4, "translate");
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(5, "input", 3);
        i0.ɵɵlistener("ngModelChange", function ParticipantDropdownComponent_Template_input_ngModelChange_5_listener($event) { return ctx.searchPartcipant($event); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵtemplate(6, ParticipantDropdownComponent_div_6_Template, 10, 5, "div", 4);
        i0.ɵɵtemplate(7, ParticipantDropdownComponent_div_7_Template, 3, 6, "div", 5);
        i0.ɵɵelementStart(8, "ul", 6);
        i0.ɵɵtemplate(9, ParticipantDropdownComponent_li_9_Template, 6, 3, "li", 7);
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(3);
        i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(4, 5, "participant-dropdown:search-contestants", i0.ɵɵpureFunction1(8, _c0$1, ctx.isTeam ? 1 : 0)), " ");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngModel", ctx.searchText);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.searchText && ctx.ownContestant);
        i0.ɵɵadvance(1);
        i0.ɵɵproperty("ngIf", !ctx.searchText);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngForOf", ctx.filteredParticipants);
    } }, directives: [i1.DefaultValueAccessor, i1.NgControlStatus, i1.NgModel, i2.AutofocusDirective, i3.NgIf, i3.NgForOf, i2.CountryFlagComponent], pipes: [i4.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ParticipantDropdownComponent, [{
        type: Component,
        args: [{
                selector: "cup-page-participant-dropdown",
                templateUrl: "./participant-dropdown.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], null, { participants: [{
            type: Input
        }], ownContestant: [{
            type: Input
        }], isTeam: [{
            type: Input
        }], selected: [{
            type: Output
        }] }); })();

function ParticipantSwitcherComponent_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function ParticipantSwitcherComponent_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function ParticipantSwitcherComponent_ng_container_4_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainer(0);
} }
function ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_cup_page_participant_dropdown_5_Template(rf, ctx) { if (rf & 1) {
    const _r15 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "cup-page-participant-dropdown", 8);
    i0.ɵɵlistener("selected", function ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_cup_page_participant_dropdown_5_Template_cup_page_participant_dropdown_selected_0_listener($event) { i0.ɵɵrestoreView(_r15); const ctx_r14 = i0.ɵɵnextContext(4); return ctx_r14.selectParticipant($event); });
    i0.ɵɵpipe(1, "async");
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const participants_r12 = i0.ɵɵnextContext().ngIf;
    const ctx_r13 = i0.ɵɵnextContext(3);
    i0.ɵɵproperty("ownContestant", i0.ɵɵpipeBind1(1, 3, ctx_r13.ownContestant$).entry)("isTeam", i0.ɵɵpipeBind1(2, 5, ctx_r13.isTeamLeague$))("participants", participants_r12);
} }
const _c0 = function (a0) { return { page: a0 }; };
function ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_Template(rf, ctx) { if (rf & 1) {
    const _r18 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementStart(3, "button", 6);
    i0.ɵɵlistener("click", function ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_Template_button_click_3_listener($event) { i0.ɵɵrestoreView(_r18); const ctx_r17 = i0.ɵɵnextContext(3); ctx_r17.toggleDropdown(); return $event.stopPropagation(); });
    i0.ɵɵtext(4);
    i0.ɵɵelementEnd();
    i0.ɵɵtemplate(5, ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_cup_page_participant_dropdown_5_Template, 3, 7, "cup-page-participant-dropdown", 7);
    i0.ɵɵpipe(6, "async");
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const participants_r12 = ctx.ngIf;
    const selectedParticipant_r10 = i0.ɵɵnextContext().ngIf;
    const ctx_r11 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 3, "participant-switcher:viewing-as", i0.ɵɵpureFunction1(8, _c0, ctx_r11.pageType)), " ");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", participants_r12[selectedParticipant_r10].name, " ");
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(6, 6, ctx_r11.isDropDownOpen$));
} }
function ParticipantSwitcherComponent_ng_template_6_ng_container_0_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, ParticipantSwitcherComponent_ng_template_6_ng_container_0_div_1_Template, 7, 10, "div", 4);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r8 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(2, 1, ctx_r8.participants$).entry);
} }
function ParticipantSwitcherComponent_ng_template_6_ng_container_2_div_1_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 5);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementStart(3, "button", 9);
    i0.ɵɵtext(4);
    i0.ɵɵpipe(5, "translate");
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r20 = i0.ɵɵnextContext(3);
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 2, "participant-switcher:viewing-as", i0.ɵɵpureFunction1(7, _c0, ctx_r20.pageType)), " ");
    i0.ɵɵadvance(3);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind1(5, 5, "participant-switcher:viewing-as-no-one"), " ");
} }
function ParticipantSwitcherComponent_ng_template_6_ng_container_2_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementContainerStart(0);
    i0.ɵɵtemplate(1, ParticipantSwitcherComponent_ng_template_6_ng_container_2_div_1_Template, 6, 9, "div", 4);
    i0.ɵɵpipe(2, "async");
    i0.ɵɵelementContainerEnd();
} if (rf & 2) {
    const ctx_r9 = i0.ɵɵnextContext(2);
    i0.ɵɵadvance(1);
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(2, 1, ctx_r9.participants$).entry);
} }
function ParticipantSwitcherComponent_ng_template_6_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵtemplate(0, ParticipantSwitcherComponent_ng_template_6_ng_container_0_Template, 3, 3, "ng-container", 3);
    i0.ɵɵpipe(1, "async");
    i0.ɵɵtemplate(2, ParticipantSwitcherComponent_ng_template_6_ng_container_2_Template, 3, 3, "ng-container", 3);
    i0.ɵɵpipe(3, "async");
} if (rf & 2) {
    const ctx_r4 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 2, ctx_r4.selectedParticipant$));
    i0.ɵɵadvance(2);
    i0.ɵɵproperty("ngIf", !i0.ɵɵpipeBind1(3, 4, ctx_r4.selectedParticipant$));
} }
function ParticipantSwitcherComponent_ng_template_8_Template(rf, ctx) { if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 10);
    i0.ɵɵtext(1);
    i0.ɵɵpipe(2, "translate");
    i0.ɵɵelementStart(3, "button", 9);
    i0.ɵɵelement(4, "div", 11);
    i0.ɵɵelementEnd();
    i0.ɵɵelementEnd();
} if (rf & 2) {
    const ctx_r7 = i0.ɵɵnextContext();
    i0.ɵɵadvance(1);
    i0.ɵɵtextInterpolate1(" ", i0.ɵɵpipeBind2(2, 3, "participant-switcher:viewing-as", i0.ɵɵpureFunction1(6, _c0, ctx_r7.pageType)), " ");
    i0.ɵɵadvance(3);
    i0.ɵɵstyleProp("width", 100, "px");
} }
class ParticipantSwitcherComponent {
    constructor(store) {
        this.store = store;
        this.isDropDownOpen$ = new BehaviorSubject(false);
    }
    ngOnInit() {
        this.store.dispatch(new LoadLeagueOnceAction(this.leagueId));
        this.league$ = this.store.select(getLeague(this.leagueId));
        this.isTeamLeague$ = this.league$.pipe(filter(entryAvailable), map(league => isTeam(league.entry)));
        this.selectedParticipant$ = this.store.pipe(getSelectedParticipant(this.leagueId), filter(exists));
        this.store.dispatch(new LoadParticipantsOnceAction(this.leagueId));
        this.participants$ = this.store.select(getLeagueParticipants(this.leagueId)).pipe(filter(exists), map(entry => (Object.assign(Object.assign({}, entry), { entry: entry.entry ? keyBy(entry.entry.filter(isCheckedIn), p => p.id) : entry.entry }))));
        this.ownContestant$ = this.store.select(getUser).pipe(filter(user => Boolean(!user.loading || user.entry)), map(user => user.entry), withLatestFrom(this.participants$.pipe(filter(entryAvailable))), switchMap(([user, participants]) => {
            if (!user || !participants.entry[+user.id]) {
                return of({
                    loading: false,
                    entry: null,
                    error: null,
                });
            }
            this.store.dispatch(new LoadContestantsOnceAction(this.leagueId, +user.id));
            return this.store.select(getContestants(this.leagueId, +user.id)).pipe(map(contestants => (Object.assign(Object.assign({}, contestants), { entry: contestants.entry && contestants.entry.length ? +contestants.entry[0].id : contestants.entry }))));
        }));
        this.isLoading$ = this.getIsLoading();
        this.hasError$ = this.getHasError();
        this.isReady$ = this.getIsReady();
    }
    selectParticipant(participantId) {
        this.store.dispatch(new SelectParticipantAction(this.leagueId, participantId));
        this.closeDropdown();
    }
    toggleDropdown() {
        this.isDropDownOpen$.next(!this.isDropDownOpen$.getValue());
    }
    closeDropdown(event) {
        if (event && event.which !== ESCAPE) {
            // 27 = esc key
            return;
        }
        this.isDropDownOpen$.next(false);
    }
    getIsReady() {
        return combineLatest(this.isLoading$, this.hasError$).pipe(map(([loading, error]) => Boolean(!loading && !error)));
    }
    getHasError() {
        return combineLatest(this.league$, this.participants$, this.ownContestant$).pipe(map(data => {
            return data.filter(entry => Boolean(entry && entry.error)).length > 0;
        }));
    }
    getIsLoading() {
        return combineLatest(this.league$, this.participants$, this.ownContestant$).pipe(map(data => {
            return data.filter(entry => Boolean(!entry || (entry.loading && !entry.entry))).length !== 0;
        }));
    }
}
ParticipantSwitcherComponent.ɵfac = function ParticipantSwitcherComponent_Factory(t) { return new (t || ParticipantSwitcherComponent)(i0.ɵɵdirectiveInject(i1$1.Store)); };
ParticipantSwitcherComponent.ɵcmp = i0.ɵɵdefineComponent({ type: ParticipantSwitcherComponent, selectors: [["cup-page-participant-switcher"]], hostBindings: function ParticipantSwitcherComponent_HostBindings(rf, ctx) { if (rf & 1) {
        i0.ɵɵlistener("keydown", function ParticipantSwitcherComponent_keydown_HostBindingHandler($event) { return ctx.closeDropdown($event); }, false, i0.ɵɵresolveDocument)("keypress", function ParticipantSwitcherComponent_keypress_HostBindingHandler($event) { return ctx.closeDropdown($event); }, false, i0.ɵɵresolveDocument)("click", function ParticipantSwitcherComponent_click_HostBindingHandler() { return ctx.closeDropdown(); });
    } }, inputs: { leagueId: "leagueId", pageType: "pageType" }, decls: 11, vars: 12, consts: [[4, "ngIf", "ngIfThen"], ["ready", ""], ["loading", "", "error", ""], [4, "ngIf"], ["class", "c-participant-switcher", 4, "ngIf"], [1, "c-participant-switcher"], [1, "c-participant-switcher__name", 3, "click"], [3, "ownContestant", "isTeam", "participants", "selected", 4, "ngIf"], [3, "ownContestant", "isTeam", "participants", "selected"], [1, "c-participant-switcher__name"], [1, "c-participant-switcher", "c-skeleton"], [1, "c-skeleton__text"]], template: function ParticipantSwitcherComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵtemplate(0, ParticipantSwitcherComponent_ng_container_0_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(1, "async");
        i0.ɵɵtemplate(2, ParticipantSwitcherComponent_ng_container_2_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(3, "async");
        i0.ɵɵtemplate(4, ParticipantSwitcherComponent_ng_container_4_Template, 1, 0, "ng-container", 0);
        i0.ɵɵpipe(5, "async");
        i0.ɵɵtemplate(6, ParticipantSwitcherComponent_ng_template_6_Template, 4, 6, "ng-template", null, 1, i0.ɵɵtemplateRefExtractor);
        i0.ɵɵtemplate(8, ParticipantSwitcherComponent_ng_template_8_Template, 5, 8, "ng-template", null, 2, i0.ɵɵtemplateRefExtractor);
    } if (rf & 2) {
        const _r3 = i0.ɵɵreference(7);
        const _r5 = i0.ɵɵreference(9);
        const _r6 = i0.ɵɵreference(10);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(1, 6, ctx.isReady$))("ngIfThen", _r3);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(3, 8, ctx.isLoading$))("ngIfThen", _r5);
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("ngIf", i0.ɵɵpipeBind1(5, 10, ctx.hasError$))("ngIfThen", _r6);
    } }, directives: [i3.NgIf, ParticipantDropdownComponent], pipes: [i3.AsyncPipe, i4.TranslatePipe], encapsulation: 2, changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(ParticipantSwitcherComponent, [{
        type: Component,
        args: [{
                selector: "cup-page-participant-switcher",
                templateUrl: "./participant-switcher.component.html",
                encapsulation: ViewEncapsulation.Emulated,
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1$1.Store }]; }, { leagueId: [{
            type: Input
        }], pageType: [{
            type: Input
        }], closeDropdown: [{
            type: HostListener,
            args: ["document:keydown", ["$event"]]
        }, {
            type: HostListener,
            args: ["document:keypress", ["$event"]]
        }, {
            type: HostListener,
            args: ["click"]
        }] }); })();

const participantSwitchterTexts = {
    "participant-switcher:viewing-as": "{page , select, pathView{You're viewing as} other{Jump to}}",
    "participant-switcher:viewing-as-no-one": "No one",
};

const participantDropdownTexts = {
    "participant-dropdown:me": "You:",
    "participant-dropdown:top-contestants": "Top {isTeam, select, 1{teams} other{players}}:",
    "participant-dropdown:search-contestants": "Search for {isTeam, select, 1{team} other{player}}:",
};

/**
 * Set selected participant for a league
 *
 * @param state
 * @param action
 */
const reducer = (state = Map(), action) => {
    if (action.type !== SelectedParticipantActionTypes.SELECT) {
        return state;
    }
    const lAction = action;
    return state.set(lAction.leagueId, lAction.participantId);
};
function selectedParticipantReducer(state, action) {
    return reducer(state, action);
}

const reducers = {
    selectedParticipant: selectedParticipantReducer,
};

function getQueryParameters() {
    const query = location.search.substr(1);
    const result = {};
    query.split("&").forEach((part) => {
        const item = part.split("=");
        result[item[0]] = decodeURIComponent(item[1]);
    });
    return result;
}
class LocationService {
    /**
     * Updates the participant id on the url if needed.
     *
     * @param id
     */
    updateContestantParameter(id) {
        if (this.getParticipantFromParameter() === id) {
            return;
        }
        const { location: { origin, pathname }, } = window;
        window.history.replaceState("", "", `${origin}${pathname}?contestantId=${id}`);
    }
    /**
     * Fetch the participant id from the url.
     * If nothing is found (or invalid result) this will return null.
     */
    getParticipantFromParameter() {
        const params = getQueryParameters();
        if (params.contestantId && +params.contestantId) {
            return +params.contestantId;
        }
        return null;
    }
}
LocationService.ɵfac = function LocationService_Factory(t) { return new (t || LocationService)(); };
LocationService.ɵprov = i0.ɵɵdefineInjectable({ token: LocationService, factory: LocationService.ɵfac });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(LocationService, [{
        type: Injectable
    }], null, null); })();

class SelectedParticipantEffect {
    constructor(actions$, store, location) {
        this.actions$ = actions$;
        this.store = store;
        this.location = location;
        this.initParticipant$ = this.actions$.pipe(ofType(SelectedParticipantActionTypes.INIT), mergeMap((action) => {
            const nextSearch$ = this.actions$.pipe(ofType(SelectedParticipantActionTypes.INIT), filter((action2) => action.leagueId === action2.leagueId), skip(1));
            const alreadySet$ = this.actions$.pipe(ofType(SelectedParticipantActionTypes.SELECT), filter((action2) => action.leagueId === action2.leagueId));
            return this.getParticipant(action.leagueId, action.selector).pipe(takeUntil(nextSearch$), takeUntil(alreadySet$), filter(Boolean), take(1), map((participantId) => new SelectParticipantAction(action.leagueId, participantId)));
        }));
        this.selectParticipant$ = this.actions$.pipe(ofType(SelectedParticipantActionTypes.SELECT), map((action) => action.participantId), filter(Boolean), tap((id) => this.location.updateContestantParameter(id)));
    }
    getHighestSeedParticipant(leagueId) {
        return this.store.select(getLeagueParticipants(leagueId)).pipe(filter(entryAvailable), distinct(entry => entry.entry), distinct(participants => {
            const playingParticipant = participants.filter(isCheckedIn);
            const highestSeedParticipant = minBy(playingParticipant, (participant) => {
                return participant.seed;
            });
            return highestSeedParticipant ? highestSeedParticipant.id : null;
        }));
    }
    getOwnContestant(leagueId) {
        return this.store.select(getUser).pipe(filter(user => Boolean(user.entry || !user.loading)), switchMap(user => {
            if (!user.entry) {
                return [null];
            }
            return this.store.pipe(distinct(getContestants(leagueId, +user.entry.id)), filter(entryAvailable), distinct(contestants => contestants.entry[0]), distinct(contestant => (contestant ? contestant.id : null)));
        }));
    }
    /**
     * Gets the correct participant id from the available data.
     *
     * If a contestant is set in the URL, use this one.
     * If you are playing select this one.
     *
     * @param action
     */
    getParticipant(leagueId, selector) {
        return combineLatest(of(this.location.getParticipantFromParameter()), this.getOwnContestant(leagueId), selector ? selector(leagueId) : of(null), this.getHighestSeedParticipant(leagueId)).pipe(switchMap(contestants => getSelectedParticipant(leagueId, contestants)(this.store)));
    }
}
SelectedParticipantEffect.ɵfac = function SelectedParticipantEffect_Factory(t) { return new (t || SelectedParticipantEffect)(i0.ɵɵinject(i1$2.Actions), i0.ɵɵinject(i1$1.Store), i0.ɵɵinject(LocationService)); };
SelectedParticipantEffect.ɵprov = i0.ɵɵdefineInjectable({ token: SelectedParticipantEffect, factory: SelectedParticipantEffect.ɵfac });
__decorate([
    Effect(),
    __metadata("design:type", Object)
], SelectedParticipantEffect.prototype, "initParticipant$", void 0);
__decorate([
    Effect({ dispatch: false }),
    __metadata("design:type", Object)
], SelectedParticipantEffect.prototype, "selectParticipant$", void 0);
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SelectedParticipantEffect, [{
        type: Injectable
    }], function () { return [{ type: i1$2.Actions }, { type: i1$1.Store }, { type: LocationService }]; }, { initParticipant$: [], selectParticipant$: [] }); })();

class SelectedParticipantModule {
}
SelectedParticipantModule.ɵfac = function SelectedParticipantModule_Factory(t) { return new (t || SelectedParticipantModule)(); };
SelectedParticipantModule.ɵmod = i0.ɵɵdefineNgModule({ type: SelectedParticipantModule });
SelectedParticipantModule.ɵinj = i0.ɵɵdefineInjector({ providers: [LocationService], imports: [[CommonModule, EffectsModule.forFeature([SelectedParticipantEffect])]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(SelectedParticipantModule, { imports: [CommonModule, i1$2.EffectsFeatureModule] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(SelectedParticipantModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule, EffectsModule.forFeature([SelectedParticipantEffect])],
                declarations: [],
                providers: [LocationService],
            }]
    }], null, null); })();

class CupPageModule {
}
CupPageModule.ɵfac = function CupPageModule_Factory(t) { return new (t || CupPageModule)(); };
CupPageModule.ɵmod = i0.ɵɵdefineNgModule({ type: CupPageModule });
CupPageModule.ɵinj = i0.ɵɵdefineInjector({ imports: [[
            CommonModule,
            FormsModule,
            StoreModule.forFeature("cupPage", reducers),
            CommonsErrorModule,
            CommonsLoadingModule,
            TranslationModule,
            StateModule,
            CommonsComponentModule,
            TranslationModule,
            SelectedParticipantModule,
        ]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(CupPageModule, { declarations: [ParticipantSwitcherComponent, ParticipantDropdownComponent], imports: [CommonModule,
        FormsModule, i1$1.StoreFeatureModule, CommonsErrorModule,
        CommonsLoadingModule,
        TranslationModule,
        StateModule,
        CommonsComponentModule,
        TranslationModule,
        SelectedParticipantModule], exports: [ParticipantSwitcherComponent, ParticipantDropdownComponent] }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(CupPageModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    StoreModule.forFeature("cupPage", reducers),
                    CommonsErrorModule,
                    CommonsLoadingModule,
                    TranslationModule,
                    StateModule,
                    CommonsComponentModule,
                    TranslationModule,
                    SelectedParticipantModule,
                ],
                declarations: [ParticipantSwitcherComponent, ParticipantDropdownComponent],
                exports: [ParticipantSwitcherComponent, ParticipantDropdownComponent],
            }]
    }], null, null); })();

/*
 * Public API Surface of cup-page
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CupPageModule, InitSelectedParticipantAction, LocationService, ParticipantDropdownComponent, ParticipantSwitcherComponent, SelectParticipantAction, SelectedParticipantActionTypes, SelectedParticipantEffect, SelectedParticipantModule, getParticipantFromState, getSelectedParticipant, isCheckedIn, isPlaying, participantDropdownTexts, participantSwitchterTexts, reducers, selectedParticipantReducer };
//# sourceMappingURL=play-cup-page.js.map
